import { Capacitor } from '@capacitor/core'
import { LocalNotifications } from '@capacitor/local-notifications'
import { Preferences } from '@capacitor/preferences'

/**
 * The morning nudge.
 *
 * A daily local notification at 9am telling the user their headlines are ready
 * to browse. It generates nothing: the actual headline pull happens when they
 * open the app, which is the whole point of the on-demand model. A client who
 * ignores the nudge costs us nothing.
 *
 * LOCAL rather than push, deliberately. Push is only warranted when the server
 * knows something the device does not, and this message is the same for
 * everyone. Going local means no APNs key, no google-services.json, no device
 * token registry, and no server job. It also means the schedule runs on the
 * DEVICE clock, so "9am their time" needs no timezone handling at all, and it
 * follows them if they travel.
 *
 * Push stays reserved for breaking news, where the server genuinely discovers
 * something first.
 */

const NUDGE_ID = 1001
const PREF_KEY = 'gogrov.nudge.enabled'

/** The copy is deliberately about availability, not production. Nothing has
 *  been generated when this fires, so a claim that new stories are "live" would
 *  be untrue, and thin results afterwards would make it a broken promise.
 *  "Ready to browse" is true the moment they tap it. */
const TITLE = 'Your headlines are ready'
const BODY = 'Today’s local stories are waiting. Swipe through and pick what to build.'

export const NUDGE_HOUR = 9

/** Local notifications are native only. On web this is all a no-op. */
export function nudgeSupported(): boolean {
  return Capacitor.isNativePlatform()
}

export async function isNudgeEnabled(): Promise<boolean> {
  if (!nudgeSupported()) return false
  const { value } = await Preferences.get({ key: PREF_KEY })
  // Default on: a daily morning prompt is the product working as intended, and
  // it is one tap to turn off.
  return value === null ? true : value === 'true'
}

async function setPref(enabled: boolean): Promise<void> {
  await Preferences.set({ key: PREF_KEY, value: String(enabled) })
}

/**
 * Ensures the daily 9am notification is scheduled.
 *
 * Safe to call on every launch: it cancels any existing copy first, so a user
 * cannot accumulate duplicates by opening the app repeatedly.
 */
export async function scheduleNudge(): Promise<boolean> {
  if (!nudgeSupported()) return false

  let perm = await LocalNotifications.checkPermissions()
  if (perm.display === 'prompt' || perm.display === 'prompt-with-rationale') {
    perm = await LocalNotifications.requestPermissions()
  }
  if (perm.display !== 'granted') return false

  await cancelNudge()

  await LocalNotifications.schedule({
    notifications: [
      {
        id: NUDGE_ID,
        title: TITLE,
        body: BODY,
        // `on` with only hour and minute repeats every day at that local time.
        schedule: { on: { hour: NUDGE_HOUR, minute: 0 } },
        // Deliberately NOT allowWhileIdle: that path wants SCHEDULE_EXACT_ALARM
        // on Android 12+, and a morning nudge does not need to be exact to the
        // second. Not worth a scary extra permission prompt.
      },
    ],
  })

  await setPref(true)
  return true
}

export async function cancelNudge(): Promise<void> {
  if (!nudgeSupported()) return
  try {
    const pending = await LocalNotifications.getPending()
    const ours = pending.notifications.filter((n) => n.id === NUDGE_ID)
    if (ours.length > 0) await LocalNotifications.cancel({ notifications: ours })
  } catch {
    // Nothing pending, or the OS refused to list. Either way there is nothing
    // to cancel and this must not break sign-in.
  }
}

export async function disableNudge(): Promise<void> {
  await cancelNudge()
  await setPref(false)
}

/**
 * Called once after sign-in. Re-arms the schedule if the user has it on.
 *
 * Re-arming matters: a device reboot or an app update can clear pending
 * notifications on some Android builds, so scheduling only at first run would
 * silently stop working.
 */
export async function syncNudge(): Promise<void> {
  if (!nudgeSupported()) return
  if (await isNudgeEnabled()) {
    await scheduleNudge()
  } else {
    await cancelNudge()
  }
}
